import { useMemo } from "react";
import {
  ArrowDownLeft,
  ArrowUpRight,
  Send,
  ShoppingCart,
  ExternalLink,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { showInfo } from "@/lib/toast";
import { C } from "./profileTheme";
import { useTranslation } from "react-i18next";
import { useWithdrawAndRechargeRecord } from "@/hooks/api/useWithdrawAndRechargeRecord";
import { formatEtherWithDecimals } from "@/lib/utils";
import type { WithdrawAndRechargeRecordItem } from "@/api/types";
import { USDT_ADDRESS, CCC_FUSD_ADDRESS, isDevelopment } from "@/config/env";
import dayjs from "dayjs";
import { getTokenName } from "@/config/common";

/** 与 GetWithdrawAndRechargeRecordRequest.type 一致：1 充值 2 提现 3 购买 4 fortuna 划转（5/6 转入转出暂不接入 Tab） */
const BILL_TAB_API_TYPE: Record<"充值" | "提现" | "划转" | "购买", number> = {
  充值: 1,
  提现: 2,
  购买: 3,
  划转: 4,
};

function shortHash(hash: string) {
  if (!hash || hash.length < 14) return hash || "--";
  return `${hash.slice(0, 6)}…${hash.slice(-6)}`;
}

function explorerTxUrl(chainId: number, txHash: string) {
  const cid = Number(chainId) || (isDevelopment ? 97 : 56);
  const base =
    cid === 97 ? "https://testnet.bscscan.com" : "https://bscscan.com";
  return `${base}/tx/${txHash}`;
}

function tokenLabel(addr: string): string {
  const a = (addr || "").toLowerCase();
  if (a) {
    return getTokenName(addr);
  }
  if (!a) return "";
  return `${addr.slice(0, 6)}…`;
}

/** 1 充值、3 购买、4 fortuna划转(转入)、5 转入 为收入侧（绿）；2 提现、6 转出 为支出侧（红） */
function isIncomingType(type: number): boolean {
  return type === 1 || type === 3 || type === 4 || type === 5;
}

function iconCategory(apiType: number): "充值" | "提现" | "划转" | "购买" {
  if (apiType === 1) return "充值";
  if (apiType === 2) return "提现";
  if (apiType === 3) return "购买";
  return "划转";
}

export function BillDialog({
  open,
  onClose,
  type,
}: {
  open: boolean;
  onClose: () => void;
  type: "充值" | "提现" | "划转" | "购买";
}) {
  const { t } = useTranslation();
  const apiType = BILL_TAB_API_TYPE[type];
  const {
    data: rawList,
    isLoading,
    isError,
  } = useWithdrawAndRechargeRecord(apiType, { enabled: open });

  const typeLabel: Record<string, string> = {
    充值: t("profilePage.bills.deposit"),
    提现: t("profilePage.bills.withdraw"),
    划转: t("profilePage.bills.transfer"),
    购买: t("profilePage.bills.purchase"),
  };

  const sortedHistory = useMemo(() => {
    if (!rawList?.length) return [];
    return [...rawList].sort((a, b) => b.createTime - a.createTime);
  }, [rawList]);

  const displayType = (apiType: number) =>
    t(`profilePage.billRecordTypes.${apiType}`, {
      defaultValue: String(apiType),
    });

  const statusText = (status: number) => {
    if (status === 1) return t("profilePage.billDialog.statusSuccess");
    if (status === -1) return t("profilePage.billDialog.statusFailed");
    return t("profilePage.billDialog.statusPending");
  };

  const statusColor = (status: number) => {
    if (status === 1) return C.green;
    if (status === -1) return C.red;
    return C.amber;
  };

  const openExplorer = (txId: string, chainId: number) => {
    if (!txId) return;
    window.open(explorerTxUrl(chainId, txId), "_blank", "noopener,noreferrer");
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        className="max-w-sm mx-auto"
        style={{
          background: "#0d0f14",
          border: "1px solid rgba(34,197,94,0.20)",
          color: C.text,
        }}
      >
        <DialogHeader>
          <DialogTitle style={{ color: C.primary }}>
            {t("profilePage.billDialog.title", { type: typeLabel[type] })}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {isLoading ? (
            <div className="text-center py-8 text-xs" style={{ color: C.dim }}>
              {t("profilePage.billDialog.loading")}
            </div>
          ) : isError ? (
            <div className="text-center py-8 text-xs" style={{ color: C.red }}>
              {t("profilePage.billDialog.fetchError")}
            </div>
          ) : sortedHistory.length === 0 ? (
            <div className="text-center py-8 text-xs" style={{ color: C.dim }}>
              {t("profilePage.billDialog.empty", { type: typeLabel[type] })}
            </div>
          ) : (
            sortedHistory.map((tx: WithdrawAndRechargeRecordItem) => {
              const incoming = isIncomingType(tx.type);
              const iconKey = iconCategory(tx.type);
              const iconBg = incoming
                ? "rgba(34,197,94,0.10)"
                : "rgba(239,68,68,0.10)";
              const iconColor = incoming ? C.green : C.red;
              const amountColor = incoming ? C.green : C.red;
              const sym = tokenLabel(tx.coinContract);
              const amt = formatEtherWithDecimals(tx.amount);
              let costNonZero = false;
              try {
                costNonZero = BigInt(tx.costAmount || "0") !== BigInt(0);
              } catch {
                costNonZero = false;
              }
              const costFormatted = formatEtherWithDecimals(
                tx.costAmount || "0"
              );
              const costPrefix = tx.type === 3 ? "+" : "-";
              const timeStr = dayjs(tx.createTime).format("YYYY-MM-DD HH:mm");

              return (
                <div
                  key={tx.outTradeNo}
                  className="rounded-lg p-3"
                  style={{
                    background: C.card,
                    border: `1px solid ${C.border}`,
                  }}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div
                        className="w-7 h-7 rounded-lg flex items-center justify-center"
                        style={{
                          background: iconBg,
                          border: `1px solid ${iconColor}22`,
                        }}
                      >
                        {iconKey === "充值" && (
                          <ArrowDownLeft
                            size={13}
                            style={{ color: iconColor }}
                          />
                        )}
                        {iconKey === "提现" && (
                          <ArrowUpRight
                            size={13}
                            style={{ color: iconColor }}
                          />
                        )}
                        {iconKey === "划转" && (
                          <Send size={13} style={{ color: iconColor }} />
                        )}
                        {iconKey === "购买" && (
                          <ShoppingCart
                            size={13}
                            style={{ color: iconColor }}
                          />
                        )}
                      </div>
                      <div>
                        <div className="text-xs font-semibold">
                          {displayType(tx.type)}
                        </div>
                        <div className="text-[10px]" style={{ color: C.dim }}>
                          {timeStr}
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div
                        className="mono-num font-bold text-sm"
                        style={{ color: amountColor }}
                      >
                        {incoming ? "+" : "-"}
                        {amt} {sym}
                      </div>
                      {costNonZero && (
                        <div
                          className="mono-num text-xs"
                          style={{ color: C.amber }}
                        >
                          {costPrefix}
                          {costFormatted} {tokenLabel(tx.costCoinContract)}
                        </div>
                      )}
                      <div
                        className="text-[10px]"
                        style={{ color: statusColor(tx.status) }}
                      >
                        {statusText(tx.status)}
                      </div>
                    </div>
                  </div>
                  {type === "划转" ? (
                    !tx.txId ? (
                      <button
                        type="button"
                        className="flex items-center gap-1 text-[10px]"
                        style={{ color: C.dim }}
                        onClick={() => showInfo(`outTradeNo: ${tx.outTradeNo}`)}
                      >
                        <span className="mono-num truncate max-w-[180px]">
                          {tx.outTradeNo}
                        </span>
                      </button>
                    ) : null
                  ) : tx.txId ? (
                    <button
                      type="button"
                      className="flex items-center gap-1 text-[10px] w-full text-left"
                      style={{ color: C.primary }}
                      onClick={() => openExplorer(tx.txId, Number(tx.chainId))}
                    >
                      <span>
                        {t("profilePage.billDialog.txHash")}:{" "}
                        <span className="mono-num">{shortHash(tx.txId)}</span>
                      </span>
                      <ExternalLink size={9} />
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="flex items-center gap-1 text-[10px]"
                      style={{ color: C.dim }}
                      onClick={() => showInfo(`outTradeNo: ${tx.outTradeNo}`)}
                    >
                      <span className="mono-num truncate max-w-[180px]">
                        {tx.outTradeNo}
                      </span>
                    </button>
                  )}
                  {type !== "划转" && tx.bscTxId ? (
                    <button
                      type="button"
                      className="flex items-center gap-1 text-[10px] mt-1"
                      style={{ color: C.primary }}
                      onClick={() =>
                        openExplorer(tx.bscTxId, isDevelopment ? 97 : 56)
                      }
                    >
                      <span>
                        BSC {t("profilePage.billDialog.txHash")}:{" "}
                        <span className="mono-num">
                          {shortHash(tx.bscTxId)}
                        </span>
                      </span>
                      <ExternalLink size={9} />
                    </button>
                  ) : null}
                </div>
              );
            })
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
